import { motion } from 'framer-motion';
import { getStageSubtitle } from '@/lib/transcriptionProgress';

interface ProgressIndicatorProps {
  progress: number;
  status: string;
}

export function ProgressIndicator({ progress, status }: ProgressIndicatorProps) {
  const subtitle = getStageSubtitle(status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-4xl mx-auto py-12 space-y-6"
    >
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <span
              className="block text-sm font-medium uppercase tracking-wider text-[#666666]"
              style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}
            >
              {status}
            </span>
            <div className="h-4 mt-1">
              <span className="text-xs italic text-gray-400">{subtitle}</span>
            </div>
          </div>
          <span
            className="text-2xl font-bold tabular-nums shrink-0"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {Math.round(progress)}%
          </span>
        </div>

        <div className="relative h-3 bg-[#F0F0F0] rounded-full overflow-hidden border border-gray-200">
          <motion.div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{
              background: `linear-gradient(90deg, #0066FF ${progress}%, #00C9FF ${Math.min(progress + 20, 100)}%)`,
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1],
            }}
          />
        </div>
      </div>

      {/* Processing animation */}
      <motion.div
        className="flex justify-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-[#0066FF]"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}
